import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ContentfulImage } from '../Contentful';
import { HandDrawnButton } from '../shared/HandDrawnButton/HandDrawnButton';
import './AccommodationsList.css';

interface AccommodationCard {
  id: string;
  title: string;
  description: string;
  images: string[];
  ctaLink: string;
  detailsLink: string;
}

interface CommunalAreaCard {
  title: string;
  description: string;
}

interface AccommodationsListProps {
  variant: 'atacama' | 'patagonia' | 'iguazu' | 'mendoza' | 'santa-catarina';
  heroTitle: string;
  heroImage: string;
  subheaderText: string;
  introText?: string;
  accommodations: AccommodationCard[];
  communalAreas: CommunalAreaCard[];
  communalAreasImage: string;
  fullLandscapeImage: string;
}

const AccommodationsList: React.FC<AccommodationsListProps> = ({
  variant,
  heroTitle,
  heroImage,
  subheaderText,
  introText,
  accommodations,
  communalAreas,
  communalAreasImage,
  fullLandscapeImage
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeCommunalCard, setActiveCommunalCard] = useState(0);
  const [activeCarouselIndexes, setActiveCarouselIndexes] = useState<{[key: string]: number}>({});

  const getVariantColor = () => {
    const colors = {
      'atacama': 'var(--color-atacama)',
      'patagonia': 'var(--color-patagonia)',
      'iguazu': 'var(--color-iguazu)',
      'mendoza': 'var(--color-mendoza)',
      'santa-catarina': 'var(--color-santa-catarina)'
    };
    return colors[variant] || colors.atacama;
  };

  const handleCarouselNavigation = (accommodationId: string, direction: 'prev' | 'next', imagesLength: number) => {
    setActiveCarouselIndexes(prev => {
      const currentIndex = prev[accommodationId] || 0;
      let newIndex = currentIndex;
      
      if (direction === 'prev') {
        newIndex = currentIndex === 0 ? imagesLength - 1 : currentIndex - 1;
      } else {
        newIndex = currentIndex === imagesLength - 1 ? 0 : currentIndex + 1;
      }
      
      return { ...prev, [accommodationId]: newIndex };
    });
  };

  return (
    <>
      {/* 1. Page Header Section - 450px */}
      <section className="accommodations-list-header">
        <div className="accommodations-list-header-content">
          <h1>{heroTitle}</h1>
        </div>
      </section>

      {/* 2. Full Hero Image Section - 840px */}
      <section className="accommodations-list-hero">
        <div 
          className="accommodations-list-hero-background"
          style={{ backgroundImage: `url('${heroImage}')` }}
        />
      </section>

      {/* 3. Subheader Text Section - 416px */}
      <section className="accommodations-list-subheader">
        <div className="accommodations-list-subheader-content">
          <p>{subheaderText}</p>
        </div>
      </section>

      {/* 3.5. Intro Text Section - Optional */}
      {introText && (
        <section className="accommodations-list-intro">
          <div className="accommodations-list-intro-content">
            <p>{introText}</p>
          </div>
        </section>
      )}

      {/* 4. Accommodations Gallery Section - 610px */}
      <section className="accommodations-list-gallery">
        <div className="accommodations-list-container">
          {accommodations.map((accommodation) => {
            const activeIndex = activeCarouselIndexes[accommodation.id] || 0;
            
            return (
              <div key={accommodation.id} className="accommodations-list-card">
                <div className="accommodations-list-image">
                  <ContentfulImage 
                    imageKey={`accommodation-${accommodation.id}-${activeIndex}`}
                    fallbackSrc={accommodation.images[activeIndex]}
                    alt={accommodation.title}
                  />
                  <div className="accommodations-list-controls">
                    <button 
                      className="accommodations-list-arrow prev"
                      onClick={() => handleCarouselNavigation(accommodation.id, 'prev', accommodation.images.length)}
                      style={{ backgroundColor: getVariantColor() }}
                    >
                      <svg width="5" height="10" viewBox="0 0 5 10" fill="none">
                        <path d="M4 1L1 5L4 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button 
                      className="accommodations-list-arrow next"
                      onClick={() => handleCarouselNavigation(accommodation.id, 'next', accommodation.images.length)}
                      style={{ backgroundColor: getVariantColor() }}
                    >
                      <svg width="5" height="10" viewBox="0 0 5 10" fill="none">
                        <path d="M1 1L4 5L1 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="accommodations-list-content">
                  <h3>{accommodation.title}</h3>
                  <p>{accommodation.description}</p>
                  <div className="accommodations-list-links">
                    <HandDrawnButton
                      text={t('common:shared.accommodation.bookYourStay')}
                      onClick={() => navigate(accommodation.ctaLink)}
                      variant={variant}
                      color="awasi"
                      animation="stroke-to-fill"
                      size="wide"
                      className="accommodations-list-book-button"
                    />
                    <HandDrawnButton
                      text={t('common:shared.accommodation.seeDetails')}
                      onClick={() => navigate(accommodation.detailsLink)}
                      variant={variant}
                      color="awasi"
                      animation="stroke-to-stroke"
                      size="normal"
                      className="accommodations-list-details-button"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 5. Communal Areas Section - 1159px */}
      <section className="accommodations-list-communal">
        <div className="accommodations-list-communal-header">
          <h2>{t('common:shared.accommodation.communalAreas')}</h2>
        </div>
        <div 
          className="accommodations-list-communal-image"
          style={{ backgroundImage: `url('${communalAreasImage}')` }}
        >
          <div 
            className="accommodations-list-communal-overlay"
            style={{ backgroundColor: getVariantColor() }}
          >
            <div className="accommodations-list-communal-details">
              <h3>{communalAreas[activeCommunalCard].title}</h3>
              <p>{communalAreas[activeCommunalCard].description}</p>
              <div className="accommodations-list-pagination">
                {communalAreas.map((_, index) => (
                  <button
                    key={index}
                    className={`accommodations-list-pagination-btn ${index === activeCommunalCard ? 'active' : ''}`}
                    onClick={() => setActiveCommunalCard(index)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Full Landscape Image - 882px */}
      <section className="accommodations-list-landscape">
        <div 
          className="accommodations-list-landscape-image"
          style={{ backgroundImage: `url('${fullLandscapeImage}')` }}
        />
      </section>
    </>
  );
};

export default AccommodationsList;